import { useCallback, useRef, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';

import Icon, { registerIcon } from '../Icon';
import * as barsIcon from '../Icon/icons/bars.icon';
import Popover from '../Popover';
import Menu from '../Menu';
import type { MenuItemProps } from '../Menu';
import { useEscape, useFocusWithin, useI18n } from '../../hooks';
import { createStringMatcher } from '../../utils';

import type { LinkProps } from './AppShell.types';
import { StyledTopNavItem } from './AppTopNav.styles';

registerIcon(barsIcon);

const FILTER_THRESHOLD = 5;

interface TopNavMenuItem extends MenuItemProps {
  items?: TopNavMenuItem[];
}

const linkToMenuItem = (link: LinkProps): TopNavMenuItem => {
  const userOnClick = link.onClick;
  const children = link.links?.map(linkToMenuItem);
  const hasChildren = children && children.length > 0;
  return {
    id: link.id || link.name,
    primary: link.name,
    visual: link.icon ? <Icon name={link.icon} /> : undefined,
    href: hasChildren ? undefined : link.href,
    // MenuItem uses `typeof selected !== 'boolean'` to decide expand vs click.
    selected: hasChildren ? undefined : link.active || undefined,
    onClick: userOnClick
      ? (_id, e) => {
          userOnClick(e);
        }
      : undefined,
    items: hasChildren ? children : undefined
  };
};

export interface TopNavMoreMenuProps {
  items: LinkProps[];
  label?: string;
  active?: boolean;
}

const TopNavMoreMenu = ({ items, label, active }: TopNavMoreMenuProps) => {
  const t = useI18n();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');

  const close = useCallback(() => setOpen(false), []);

  useFocusWithin<HTMLElement>(
    [triggerRef, popoverRef],
    useCallback((isFocused: boolean) => {
      if (!isFocused) setOpen(false);
    }, [])
  );

  useEscape(
    useCallback(
      (e: KeyboardEvent) => {
        if (open) {
          setOpen(false);
          e.stopPropagation();
          triggerRef.current?.focus();
        }
      },
      [open]
    )
    // No target: attach to document so Escape works when focus is inside the
    // Popover portal (events from portal content don't bubble to the trigger element).
  );

  const allItems = useMemo(() => items.map(linkToMenuItem), [items]);
  const showFilter = items.length >= FILTER_THRESHOLD;
  const filterRegex = createStringMatcher(filterValue, 'contains');
  const menuItems = showFilter
    ? allItems.filter(it => filterRegex.test(String(it.primary ?? '')))
    : allItems;

  const handleTriggerClick = () => {
    if (!open) setFilterValue('');
    setOpen(v => !v);
  };

  return (
    <>
      <StyledTopNavItem
        ref={triggerRef}
        $active={active || open}
        aria-haspopup='menu'
        aria-expanded={open}
        onClick={handleTriggerClick}
      >
        <Icon name='bars' />
        <span>{label ?? t('app_shell_more')}</span>
      </StyledTopNavItem>
      {open && (
        <Popover ref={popoverRef} target={triggerRef.current} placement='bottom-end'>
          <Menu
            mode='action'
            variant='flyout'
            aria-label={label ?? t('app_shell_more')}
            items={menuItems}
            onItemClick={close}
            focusControlEl={triggerRef.current ?? undefined}
            {...(showFilter
              ? {
                  filterInputProps: {
                    value: filterValue,
                    onChange: (e: ChangeEvent<HTMLInputElement>) => setFilterValue(e.target.value)
                  }
                }
              : {})}
          />
        </Popover>
      )}
    </>
  );
};

export default TopNavMoreMenu;
